// Medalha de conquista (docs/prototipo/conquistas-cadastro.html §2, §3 e §18; DESIGN §5.24): ícone do acervo OU
// número × estilo, desenhada em SVG pelo app (iOS, Android e painel iguais; sem imagem baixada). Porte fiel da
// função `medalha()` do mockup: mesmas formas, raios, traços e opacidades. Diferença de desenho é defeito.
//
// - Estilos: bronze, prata e ouro = moeda serrilhada; platina = hexágono lapidado; academia = placa com o corte do
//   GrindSet em esmalte (1 dos 12, `--gs-esmalte-NN`); neon = placa arredondada preta com tubo de luz.
// - Compacta (tamanho < 56): sem serrilha, listras, filete e cadeado; campo e ícone maiores; número sem legenda.
// - Bloqueada: silhueta do estilo em `recuo`, contorno `fio`, contorno interno tracejado, conteúdo em `texto-3` a
//   90 % e cadeado no canto (≥ 56).
// - Degradê só aqui dentro (é o material, DESIGN §0.4). Cores de metal e neon são tokens de ARTE (tokens.css).
// - Acessível: `rotulo` dá role="img" + aria-label (folha, onde a medalha é a informação); sem ele, aria-hidden
//   (na grade, o botão em volta leva o nome).
import { useId } from "react";
import type { CorNeon, EstiloMedalha } from "@grindset/server/contratos";
import { desenhoDoIcone, type ElementoIcone } from "./desenhos";
import "./medalhas.css";

const FONTE = "Archivo, system-ui, sans-serif";
const PLACA = "M22 8H82L112 51V98Q112 112 98 112H22Q8 112 8 98V22Q8 8 22 8Z";
const HEX = "M60 4 108.5 32v56L60 116 11.5 88V32Z";
const QUAD = "M30 6H90A24 24 0 0 1 114 30V90A24 24 0 0 1 90 114H30A24 24 0 0 1 6 90V30A24 24 0 0 1 30 6Z";
const MOEDA = "M60 4a56 56 0 1 1 0 112a56 56 0 1 1 0-112z";
const ESCALA_INTERNA = "translate(60 60) scale(.78) translate(-60 -60)";
const ESCALA_TUBO = "translate(60 60) scale(.84) translate(-60 -60)";

/** Arte (não é cor de UI): fundo e borda da placa Neon (preta também no painel) e a legenda da Platina. */
const ARTE = { neonFundo: "#0c0d0f", neonBorda: "#2a2d32", legendaPlatina: "#c4ccd6" } as const;

const metal = (estilo: "bronze" | "prata" | "ouro" | "platina") =>
  [`var(--gs-metal-${estilo}-1)`, `var(--gs-metal-${estilo}-2)`] as const;
const neon = (cor: CorNeon) => `var(--gs-neon-${cor})`;
export const esmalte = (n: number) => `var(--gs-esmalte-${String(Math.min(12, Math.max(1, n))).padStart(2, "0")})`;

function hexInterno(r: number): [number, number][] {
  const p: [number, number][] = [];
  for (let i = 0; i < 6; i++) {
    const a = ((-90 + 60 * i) * Math.PI) / 180;
    p.push([60 + r * Math.cos(a), 60 + r * Math.sin(a)]);
  }
  return p;
}
const caminho = (pontos: [number, number][]) => `M${pontos.map((p) => p.join(" ")).join("L")}Z`;

function silhueta(estilo: EstiloMedalha): string {
  if (estilo === "platina") return HEX;
  if (estilo === "academia") return PLACA;
  if (estilo === "neon") return QUAD;
  return MOEDA;
}

/** Ícone do acervo centrado em (cx, cy) com `tam` de lado; traço `sw` na escala da medalha. */
function IconeSvg({ chave, tam, cor, sw }: { chave: string | null | undefined; tam: number; cor: string; sw: number }) {
  const s = tam / 24;
  return (
    <g
      transform={`translate(${60 - tam / 2} ${60 - tam / 2}) scale(${s})`}
      fill="none"
      style={{ stroke: cor }}
      strokeWidth={sw / s}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {desenhoDoIcone(chave).map((el, i) => elemento(el, i, cor))}
    </g>
  );
}

function elemento(el: ElementoIcone, i: number, cor: string) {
  const [tag, attrs, cheio] = el;
  const estilo = cheio ? { fill: cor, stroke: "none" } : undefined;
  if (tag === "path") return <path key={i} d={attrs.d} style={estilo} />;
  if (tag === "rect") return <rect key={i} {...attrs} style={estilo} />;
  return <circle key={i} {...attrs} style={estilo} />;
}

function NumeroSvg({
  numero,
  legenda,
  cor,
  cor2,
  compacta,
}: {
  numero: string;
  legenda: string | null;
  cor: string;
  cor2: string;
  compacta: boolean;
}) {
  const n = numero.length;
  const fs = compacta ? (n > 3 ? 24 : n > 2 ? 31 : 42) : n > 3 ? 22 : n > 2 ? 27 : 32;
  const comLegenda = !compacta && !!legenda;
  return (
    <>
      <text
        x={60}
        y={comLegenda ? 64 : 60 + fs * 0.36}
        textAnchor="middle"
        fontFamily={FONTE}
        fontWeight={800}
        fontSize={fs}
        style={{ fontStretch: "115%", letterSpacing: "-.03em", fill: cor }}
      >
        {numero}
      </text>
      {comLegenda && (
        <text x={60} y={80} textAnchor="middle" fontFamily={FONTE} fontWeight={600} fontSize={10} style={{ fill: cor2 }}>
          {legenda}
        </text>
      )}
    </>
  );
}

export interface PropsMedalhaConquista {
  estilo: EstiloMedalha;
  /** Chave do acervo (centro ICONE). Desconhecida cai em `award`. */
  icone?: string | null;
  /** Centro NUMERO (vem do servidor: "100", "1º", "7h"). Tem precedência sobre o ícone. */
  numero?: string | null;
  legenda?: string | null;
  estado?: "ganha" | "bloqueada";
  tamanho?: number;
  /** Repetível: selo ×N quando > 1. */
  vezes?: number;
  /** Cor da luz (só Neon). */
  cor?: CorNeon | null;
  /** Esmalte da "Cor da academia" (1..12). */
  esmalte?: number;
  /** Faixa de brilho que atravessa a peça uma vez (celebração). */
  brilho?: boolean;
  /** Nome acessível: com ele a medalha vira role="img". */
  rotulo?: string;
  className?: string;
}

export function MedalhaConquista({
  estilo,
  icone,
  numero,
  legenda = null,
  estado = "ganha",
  tamanho = 96,
  vezes = 0,
  cor,
  esmalte: nEsmalte = 9,
  brilho,
  rotulo,
  className,
}: PropsMedalhaConquista) {
  const id = useId().replace(/:/g, "");
  const c = tamanho < 56;
  const aria = rotulo ? { role: "img" as const, "aria-label": rotulo } : { "aria-hidden": true as const };
  const svg = { width: tamanho, height: tamanho, viewBox: "0 0 120 120", className, ...aria };
  const temNumero = numero !== null && numero !== undefined && numero !== "";

  const conteudo = (corC: string, cor2: string, raio: number) =>
    temNumero ? (
      <NumeroSvg numero={numero} legenda={legenda} cor={corC} cor2={cor2} compacta={c} />
    ) : (
      <IconeSvg chave={icone} tam={c ? raio * 1.25 : raio * 1.06} cor={corC} sw={c ? 5.4 : 3.9} />
    );

  if (estado === "bloqueada") {
    const interno =
      estilo === "platina" ? (
        <path d={caminho(hexInterno(40))} fill="none" stroke="var(--gs-fio)" strokeWidth={2} strokeDasharray="4 4" />
      ) : estilo === "academia" ? (
        <path d={PLACA} fill="none" stroke="var(--gs-fio)" strokeWidth={2} strokeDasharray="4 4" transform={ESCALA_INTERNA} />
      ) : estilo === "neon" ? (
        <path d={QUAD} fill="none" stroke="var(--gs-fio)" strokeWidth={2} strokeDasharray="4 4" transform={ESCALA_INTERNA} />
      ) : (
        <circle cx={60} cy={60} r={40} fill="none" stroke="var(--gs-fio)" strokeWidth={2} strokeDasharray="4 4" />
      );
    return (
      <svg {...svg}>
        <path d={silhueta(estilo)} fill="var(--gs-recuo)" stroke="var(--gs-fio)" strokeWidth={c ? 3 : 2} strokeLinejoin="round" />
        {!c && interno}
        <g opacity={0.9}>{conteudo("var(--gs-texto-3)", "var(--gs-texto-3)", c ? 44 : 40)}</g>
        {!c && (
          <g transform="translate(88 8)">
            <circle cx={12} cy={12} r={12} fill="var(--gs-painel)" stroke="var(--gs-fio)" />
            <path
              d="M8 12h8v6H8zM9.5 12v-2a2.5 2.5 0 0 1 5 0v2"
              fill="none"
              stroke="var(--gs-texto-3)"
              strokeWidth={1.6}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        )}
      </svg>
    );
  }

  const selo =
    vezes > 1 ? (
      <g>
        <rect x={76} y={2} width={40} height={22} rx={11} fill="var(--gs-tinta)" />
        <text x={96} y={17.5} textAnchor="middle" fontFamily={FONTE} fontWeight={700} fontSize={12} fill="var(--gs-sobre-tinta)">
          ×{vezes}
        </text>
      </g>
    ) : null;
  const gradBrilho = brilho ? (
    <linearGradient id={`${id}s`} x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stopColor="white" stopOpacity={0} />
      <stop offset=".5" stopColor="white" stopOpacity={0.55} />
      <stop offset="1" stopColor="white" stopOpacity={0} />
    </linearGradient>
  ) : null;
  const faixaBrilho = brilho ? (
    <rect data-confete className="medalha-brilho" x={-60} y={-20} width={40} height={160} fill={`url(#${id}s)`} transform="rotate(20)" />
  ) : null;

  if (estilo === "bronze" || estilo === "prata" || estilo === "ouro" || estilo === "platina") {
    const [m1, m2] = metal(estilo);
    const grad = (
      <linearGradient id={`${id}g`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" style={{ stopColor: m1 }} />
        <stop offset=".55" style={{ stopColor: m2 }} />
        <stop offset="1" style={{ stopColor: m1 }} />
      </linearGradient>
    );
    if (estilo === "platina") {
      const o6 = hexInterno(56);
      const i6 = hexInterno(c ? 46 : 41);
      return (
        <svg {...svg}>
          <defs>
            {grad}
            {gradBrilho}
            <clipPath id={`${id}c`}>
              <path d={HEX} />
            </clipPath>
          </defs>
          <path d={HEX} fill={`url(#${id}g)`} stroke={`url(#${id}g)`} strokeWidth={5} strokeLinejoin="round" />
          {o6.map((p, i) => {
            const q = o6[(i + 1) % 6]!;
            const r = i6[(i + 1) % 6]!;
            const s = i6[i]!;
            const op = [0.34, 0.12, 0, 0.18, 0.06, 0.28][i]!;
            const preto = i === 2 || i === 3;
            return (
              <path
                key={i}
                d={`M${p.join(" ")}L${q.join(" ")}L${r.join(" ")}L${s.join(" ")}Z`}
                fill={preto ? "black" : "white"}
                fillOpacity={i === 2 ? 0.16 : i === 3 ? 0.08 : op}
              />
            );
          })}
          <path d={caminho(i6)} fill="var(--gs-placar)" stroke={`url(#${id}g)`} strokeWidth={2} strokeLinejoin="round" />
          {conteudo(m1, ARTE.legendaPlatina, c ? 46 : 44)}
          <g clipPath={`url(#${id}c)`}>{faixaBrilho}</g>
          {selo}
        </svg>
      );
    }
    const rIn = c ? 45 : 41;
    return (
      <svg {...svg}>
        <defs>
          {grad}
          {gradBrilho}
          <clipPath id={`${id}c`}>
            <circle cx={60} cy={60} r={56} />
          </clipPath>
        </defs>
        <circle cx={60} cy={60} r={56} fill={`url(#${id}g)`} />
        {!c &&
          Array.from({ length: 40 }, (_, i) => {
            const a = (i * 9 * Math.PI) / 180;
            return (
              <line
                key={i}
                x1={60 + 47.5 * Math.cos(a)}
                y1={60 + 47.5 * Math.sin(a)}
                x2={60 + 52 * Math.cos(a)}
                y2={60 + 52 * Math.sin(a)}
                stroke="white"
                strokeOpacity={0.38}
                strokeWidth={1.5}
              />
            );
          })}
        <path d="M60 4a56 56 0 0 1 56 56" fill="none" stroke="white" strokeOpacity={0.25} strokeWidth={2} />
        <circle cx={60} cy={60} r={rIn} fill="var(--gs-placar)" />
        <circle cx={60} cy={60} r={rIn} fill="none" stroke={`url(#${id}g)`} strokeWidth={2} />
        {estilo === "ouro" && !c && (
          <circle cx={60} cy={60} r={rIn - 4} fill="none" stroke={`url(#${id}g)`} strokeWidth={1} strokeOpacity={0.7} />
        )}
        {conteudo(m1, "var(--gs-sobre-placar-2)", 46)}
        <g clipPath={`url(#${id}c)`}>{faixaBrilho}</g>
        {selo}
      </svg>
    );
  }

  if (estilo === "academia") {
    const corE = esmalte(nEsmalte);
    return (
      <svg {...svg}>
        <defs>
          <linearGradient id={`${id}e`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" style={{ stopColor: `color-mix(in srgb, ${corE} 62%, white)` }} />
            <stop offset=".55" style={{ stopColor: corE }} />
            <stop offset="1" style={{ stopColor: `color-mix(in srgb, ${corE} 60%, black)` }} />
          </linearGradient>
          <linearGradient id={`${id}b`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" style={{ stopColor: "var(--gs-metal-ouro-1)" }} />
            <stop offset=".5" style={{ stopColor: "var(--gs-metal-ouro-2)" }} />
            <stop offset="1" style={{ stopColor: "var(--gs-metal-ouro-1)" }} />
          </linearGradient>
          <pattern id={`${id}p`} width={7} height={7} patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
            <rect width={2} height={7} fill="white" fillOpacity={0.09} />
          </pattern>
          <clipPath id={`${id}c`}>
            <path d={PLACA} />
          </clipPath>
          {gradBrilho}
        </defs>
        <path d={PLACA} fill={`url(#${id}e)`} />
        <g clipPath={`url(#${id}c)`}>
          {!c && <rect width={120} height={120} fill={`url(#${id}p)`} />}
          <path d="M0 0H120V46Q60 64 0 44Z" fill="white" fillOpacity={0.1} />
          {faixaBrilho}
        </g>
        <path d={PLACA} fill="none" stroke={`url(#${id}b)`} strokeWidth={c ? 6 : 4} />
        {!c && <path d={PLACA} fill="none" stroke="white" strokeOpacity={0.28} strokeWidth={1.2} transform={ESCALA_TUBO} />}
        {conteudo("white", "rgb(255 255 255 / .85)", c ? 48 : 52)}
        {selo}
      </svg>
    );
  }

  // Neon
  const nc = neon(cor ?? "gelo");
  const nucleo = `color-mix(in srgb, ${nc} 45%, white)`;
  const halo = c ? 1.6 : 3;
  return (
    <svg {...svg}>
      <defs>
        <filter id={`${id}f`} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation={halo} />
        </filter>
        <clipPath id={`${id}c`}>
          <path d={QUAD} />
        </clipPath>
        {gradBrilho}
      </defs>
      <path d={QUAD} fill={ARTE.neonFundo} stroke={ARTE.neonBorda} strokeWidth={2} />
      <g clipPath={`url(#${id}c)`}>
        <path d={QUAD} fill="none" style={{ stroke: nc }} strokeOpacity={0.1} strokeWidth={22} transform={ESCALA_TUBO} />
      </g>
      <g filter={`url(#${id}f)`}>
        <path d={QUAD} fill="none" style={{ stroke: nc }} strokeWidth={5} transform={ESCALA_TUBO} />
      </g>
      <path d={QUAD} fill="none" style={{ stroke: nucleo }} strokeWidth={2} transform={ESCALA_TUBO} />
      {temNumero ? (
        <>
          <g filter={`url(#${id}f)`}>
            <NumeroSvg numero={numero} legenda={null} cor={nc} cor2={nc} compacta={c} />
          </g>
          <NumeroSvg numero={numero} legenda={legenda} cor={nucleo} cor2={nc} compacta={c} />
        </>
      ) : (
        <>
          <g filter={`url(#${id}f)`}>
            <IconeSvg chave={icone} tam={c ? 58 : 52} cor={nc} sw={c ? 6 : 4.4} />
          </g>
          <IconeSvg chave={icone} tam={c ? 58 : 52} cor={nucleo} sw={c ? 3 : 2.2} />
        </>
      )}
      <g clipPath={`url(#${id}c)`}>{faixaBrilho}</g>
      {selo}
    </svg>
  );
}
